import React, { useEffect, useRef, useState } from "react";
import Loaders from "../Loaders";
import HeaderTop from "../HeaderTop";
import Menus from "../Menus";
import Footers from "../Footers";
import "../../../libs/css/bootstrap.min3.3.7.css";
import "../../../libs/css/sweetalert2.min.css";
import { API_ROUTES } from "../../../utils/constants";
import Nulls from "../Nulls";
import { format, parseISO } from "date-fns";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import FilterComponent from "./FilterComponent2";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

export default function RevenueServices(props) {
  const {
    objectToFormData,
    errorFunction,
    Helmet,
    capitalizeWords,
    isAuthExpired,
    today,
    resetTimeToMidnight,
    resetTimeToB4Midnight,
    formatDate,
    allCPs,
    isLoading1,
    userSessions,
  } = props;

  useEffect(() => {
    document.title = "Service Revenue | Forthsoft";
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [dailyBreakdown, setDailyBreakdown] = useState([]);
  const [activeTab, setActiveTab] = useState("summary");
  const [isFade, setIsFade] = useState(false);
  const [currentFilters, setCurrentFilters] = useState(null);

  const filterRef = useRef(null);

  const networkData = [
    { id: "1", name: "MTN" },
    { id: "2", name: "GLO" },
    { id: "3", name: "AIRTEL" },
    { id: "4", name: "9MOBILE" },
  ];

  const userData = JSON.parse(localStorage.getItem("userDataFs"));

  // Summary table columns
  const summaryColumns = [
    {
      name: "Metric",
      selector: (row) => row.metric,
      sortable: true,
      minWidth: "200px",
    },
    {
      name: "Value",
      selector: (row) => row.value,
      sortable: true,
    },
  ];

  // Daily breakdown columns
  const dailyColumns = [
    {
      name: "Date",
      selector: (row) => row.date,
      sortable: true,
      cell: (row) => {
        try {
          return format(parseISO(row.date), "MMM dd, yyyy");
        } catch {
          return row.date;
        }
      },
    },
    {
      name: "Daily Amount",
      selector: (row) =>
        "₦" + Number(row.dailyChargeAmount || 0).toLocaleString("en-NG"),
      sortable: true,
    },
    {
      name: "Daily Count",
      selector: (row) => Number(row.dailyCount || 0).toLocaleString("en-NG"),
      sortable: true,
    },
    {
      name: "Daily Records",
      selector: (row) => Number(row.dailyRecords || 0).toLocaleString("en-NG"),
      sortable: true,
    },
  ];

  const getData = async (dataString) => {
    setIsFade(true);

    try {
      const response = await fetch(API_ROUTES.VIEW_SERVICE_REVENUE, {
        method: "POST",
        body: objectToFormData(dataString),
        headers: { token: userData?.responseDetails?.token },
      });
      const responseJSON = await response.json();
      isAuthExpired(responseJSON);

      if (responseJSON.status.toString() === "200") {
        const { summary, breakdown } = responseJSON.data;

        setSummaryData(summary);
        setDailyBreakdown(breakdown.by_day || []);
        setCurrentFilters(dataString);

        setIsLoading(false);
        setIsFade(false);
        filterRef.current?.resetService();
      } else {
        setSummaryData(null);
        setDailyBreakdown([]);
        setCurrentFilters(null);

        setIsLoading(false);
        setIsFade(false);
        filterRef.current?.resetService();

        Swal.fire({
          title: "No Data",
          text:
            responseJSON.message || "No data found for the selected criteria",
          icon: "info",
        });
      }
    } catch (error) {
      console.log("Error getting data: ", error);
      setSummaryData(null);
      setDailyBreakdown([]);
      setCurrentFilters(null);

      setIsLoading(false);
      setIsFade(false);
      filterRef.current?.resetService();

      Swal.fire({
        title: "Error",
        text: "Failed to fetch data. Please try again.",
        icon: "error",
      });
    }
  };

  const handleDuration = async (data) => {
    if (data.operator === "") {
      Swal.fire({
        title: "Error",
        text: "Please select an Operator",
        icon: "error",
      });
      return;
    }

    if (data.service_id === "") {
      Swal.fire({
        title: "Error",
        text: "Please select a Service",
        icon: "error",
      });
      return;
    }

    let dataString = {
      operator: data.operator,
      distinct_service_id: data.service_id,
      start_date: data.fro,
      end_date: data.to,
    };

    getData(dataString);
  };

  // Prepare summary data for table display
  const summaryTableData = summaryData
    ? [
        {
          metric: "Total Amount",
          value:
            "₦" +
            Number(summaryData.total_charge_amount || 0).toLocaleString(
              "en-NG",
            ),
        },
        {
          metric: "Total Count",
          value: Number(summaryData.total_count || 0).toLocaleString("en-NG"),
        },
        { metric: "Total Days", value: summaryData.total_days || 0 },
        { metric: "Total Networks", value: summaryData.total_networks || 0 },
        { metric: "Total Products", value: summaryData.total_products || 0 },
      ]
    : [];

  // Export to CSV
  const exportToCSV = () => {
    if (!summaryData) {
      Swal.fire({
        title: "No Data",
        text: "Please filter data first before exporting",
        icon: "warning",
      });
      return;
    }

    let csvContent = "";
    const selectedService = userSessions?.find(
      (s) => s.serviceId === currentFilters?.distinct_service_id,
    );
    const selectedNetwork = networkData?.find(
      (n) => n.id === currentFilters?.operator,
    );

    // Add header information
    csvContent += `Service Revenue Report\n`;
    csvContent += `Generated: ${format(new Date(), "MMM dd, yyyy HH:mm:ss")}\n`;
    csvContent += `Network: ${selectedNetwork?.name || "N/A"}\n`;
    csvContent += `Service: ${selectedService?.serviceName || "N/A"}\n`;
    csvContent += `Period: ${currentFilters?.start_date} to ${currentFilters?.end_date}\n\n`;

    if (activeTab === "summary") {
      csvContent += "Metric,Value\n";
      summaryTableData.forEach((row) => {
        csvContent += `"${row.metric}","${row.value}"\n`;
      });
    } else if (activeTab === "daily") {
      csvContent += "Date,Daily Amount,Daily Count,Daily Records\n";
      dailyBreakdown.forEach((row) => {
        const formattedDate = format(parseISO(row.date), "MMM dd, yyyy");
        csvContent += `"${formattedDate}","₦${Number(
          row.dailyChargeAmount || 0,
        ).toLocaleString("en-NG")}","${Number(
          row.dailyCount || 0,
        ).toLocaleString("en-NG")}","${Number(
          row.dailyRecords || 0,
        ).toLocaleString("en-NG")}"\n`;
      });
    }

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `service_revenue_${activeTab}_${format(
        new Date(),
        "yyyyMMdd_HHmmss",
      )}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!summaryData) {
      Swal.fire({
        title: "No Data",
        text: "Please filter data first before exporting",
        icon: "warning",
      });
      return;
    }

    const selectedService = userSessions?.find(
      (s) => s.serviceId === currentFilters?.distinct_service_id,
    );
    const selectedNetwork = networkData?.find(
      (n) => n.id === currentFilters?.operator,
    );

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Add header information sheet
    const headerData = [
      ["Service Revenue Report"],
      ["Generated:", format(new Date(), "MMM dd, yyyy HH:mm:ss")],
      ["Network:", selectedNetwork?.name || "N/A"],
      ["Service:", selectedService?.serviceName || "N/A"],
      [
        "Period:",
        `${currentFilters?.start_date} to ${currentFilters?.end_date}`,
      ],
      [],
    ];

    if (activeTab === "summary") {
      // Add summary data
      const summarySheet = [
        ...headerData,
        ["Metric", "Value"],
        ...summaryTableData.map((row) => [row.metric, row.value]),
      ];
      const ws = XLSX.utils.aoa_to_sheet(summarySheet);
      XLSX.utils.book_append_sheet(wb, ws, "Summary");
    } else if (activeTab === "daily") {
      // Add daily data
      const dailySheet = [
        ...headerData,
        ["Date", "Daily Amount", "Daily Count", "Daily Records"],
        ...dailyBreakdown.map((row) => [
          format(parseISO(row.date), "MMM dd, yyyy"),
          `₦${Number(row.dailyChargeAmount || 0).toLocaleString("en-NG")}`,
          Number(row.dailyCount || 0).toLocaleString("en-NG"),
          Number(row.dailyRecords || 0).toLocaleString("en-NG"),
        ]),
      ];
      const ws = XLSX.utils.aoa_to_sheet(dailySheet);
      XLSX.utils.book_append_sheet(wb, ws, "Daily Breakdown");
    }

    // Save file
    XLSX.writeFile(
      wb,
      `service_revenue_${activeTab}_${format(
        new Date(),
        "yyyyMMdd_HHmmss",
      )}.xlsx`,
    );
  };

  return (
    <>
      <div className="flapt-page-wrapper mainContent">
        <Menus />

        <div className="flapt-page-content">
          <HeaderTop />

          <div className="main-content introduction-farm mt--20">
            <div className="content-wraper-area">
              <div className="dashboard-area">
                <div className="container-fluid mb-90">
                  <div className="card-header mb--30 mb-xs-10 ml-10">
                    <h2>Service Revenue</h2>
                  </div>

                  {isLoading1 ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      {/* Filter Component */}
                      <FilterComponent
                        ref={filterRef}
                        handleDuration={handleDuration}
                        allCPs={allCPs}
                        userSessions={userSessions}
                        networkData={networkData}
                        isServiceRevenue={true}
                      />

                      {summaryData && currentFilters && (
                        <div
                          className="mb-10 ml-10"
                          style={{ fontSize: "14px", color: "#666" }}
                        >
                          Showing results for{" "}
                          <strong>
                            {userSessions?.find(
                              (s) =>
                                s.serviceId ===
                                currentFilters.distinct_service_id,
                            )?.serviceName || "Unknown Service"}
                          </strong>
                        </div>
                      )}

                      <div style={{ opacity: isFade ? "0.3" : 1 }}>
                        {/* Tab Navigation and Export Buttons */}
                        {summaryData && (
                          <div className="row mb-10">
                            <div className="col-12">
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                }}
                              >
                                <div>
                                  <button
                                    className={`btn ${
                                      activeTab === "summary"
                                        ? "btn-primary"
                                        : "btn-outline-primary"
                                    } mr-2 mb-2`}
                                    onClick={() => setActiveTab("summary")}
                                  >
                                    Summary
                                  </button>
                                  <button
                                    className={`btn ${
                                      activeTab === "daily"
                                        ? "btn-primary"
                                        : "btn-outline-primary"
                                    } mr-2 mb-2`}
                                    onClick={() => setActiveTab("daily")}
                                  >
                                    Daily ({dailyBreakdown.length})
                                  </button>
                                </div>
                                <div>
                                  <button
                                    className="btn btn-success mr-2 mb-2"
                                    onClick={exportToExcel}
                                    title="Export to Excel"
                                  >
                                    <i className="fa fa-file-excel-o mr-2"></i>
                                    Export Excel
                                  </button>
                                  <button
                                    className="btn btn-info mb-2"
                                    onClick={exportToCSV}
                                    title="Export to CSV"
                                  >
                                    <i className="fa fa-file-text-o mr-2"></i>
                                    Export CSV
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Tab Content */}
                        <div className="row">
                          <div className="col-12">
                            {activeTab === "summary" && summaryData && (
                              <DataTable
                                className="revenue_datatable users"
                                title=""
                                columns={summaryColumns}
                                data={summaryTableData}
                                highlightOnHover
                              />
                            )}

                            {activeTab === "daily" &&
                              dailyBreakdown.length > 0 && (
                                <DataTable
                                  className="revenue_datatable users"
                                  title=""
                                  columns={dailyColumns}
                                  data={dailyBreakdown}
                                  highlightOnHover
                                />
                              )}

                            {!summaryData && !isLoading && (
                              <div className="mt-20">
                                Please filter on the filter boxes above to
                                populate data
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <Footers />
          </div>
        </div>
      </div>
    </>
  );
}
